import { useMemo, FC, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router';
import Measure from 'react-measure';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { ICustomOption } from 'Pages/Activities/Modals/ShowCreateActivity/useOptionsSelect';
import { IStage } from 'Data/interfaces/Obra/IObraStages';
import { IDisciplineResponse } from 'Data/interfaces/Obra/IDisciplineResponse';
import CreateActivityWithViewpoint from 'Pages/FederatedViewer/Modals/CreateActivityWithViewpoint';
import { AutodeskViewpointEngine } from 'Services/viewpoint/AutodeskViewpointEngine';
import styles from './ViewerTeste.module.scss';
import stylesDrawer from 'Pages/FederatedViewer/components/PropertiesDrawer/PropertiesDrawer.module.scss';
import PropertiesDrawer from 'Pages/FederatedViewer/components/PropertiesDrawer';
import ButtonDrawer from 'Components/UI/ButtonDrawer';
import { Icon } from 'Components/UI';
import useInternalViewer from './useInternalViewer';
import {
  ICamera,
  INode,
  IPrancha,
  IView,
} from 'Pages/FederatedViewer/useFederatedViewer';
import './viewerTeste.css';
import { IInitialViewpoint, ITopicPoint } from 'Data/interfaces/Activities/IViewpointData';

export interface IInternalViewer {
  loadUrn: string | null;
  drawerOpen: boolean;
  toggleNode: INode;
  toggleSelectNode: INode;
  toggleViewCamera: ICamera;
  toggleView: IView;
  togglePrancha: IPrancha;
  visibilityOptions: ICustomOption[];
  priorityOptions: ICustomOption[];
  priorityNoBgOptions: ICustomOption[];
  typeOptions: ICustomOption[];
  stageOptions: string[];
  pointsOptions: string[];
  communicationOptions: string[];
  userOptions: ICustomOption[];
  disciplineOptions: ICustomOption[];
  disciplines?: IDisciplineResponse[];
  stages: IStage[];
  allowOnlyStagesLinkedToDisciplinesInTopics: boolean;
  createStageLoading: boolean;
  activityViewpointFeature?: boolean;
  onLoadUrn: (urn: string | null) => void;
  onActiveUrns: (urn: string, extension: string) => void;
  onToggleNode: (node: number | null, urn: string | null) => void;
  onToggleSelectNode: (node: number | null, urn: string | null) => void;
  onTogglePrancha: (
    prancha: Autodesk.Viewing.BubbleNode | null,
    urn: string | null,
    id: number | null,
  ) => void;
  onToggleViewCamera: (
    camera: Autodesk.Viewing.BubbleNode | null,
    urn: string | null,
    id: number | null,
  ) => void;
  onToggleView: (
    view: Autodesk.Viewing.BubbleNode | null,
    urn: string | null,
    id: number | null,
  ) => void;
  onClearAllToggles: () => void;
  onModelLoadError: (urn: string) => void;
  onCreateActivityHandlerReady?: (handler: () => void) => void;
  onRegisterViewpointRestorer?: (handler: (viewpoint: IInitialViewpoint, topicPoints?: ITopicPoint[]) => void) => void;
  initialViewpoint?: IInitialViewpoint | null;
  initialTopicPoints?: ITopicPoint[] | null;
}

export interface IExtensions {
  docNow: Autodesk.Viewing.Document | null;
  viewables: Autodesk.Viewing.BubbleNode[] | null;
}

export interface IModelNode {
  urn: string | null;
  model: Autodesk.Viewing.Model | null;
}

const InternalViewer: FC<Props> = ({
  loadUrn,
  urns,
  extensionValidateds,
  unloadUrn,
  drawerOpen,
  models,
  views,
  viewpoints,
  pranchas,
  selectedProperties,
  toggleNode,
  hideNodes,
  toggleSelectNode,
  toggleViewCamera,
  toggleView,
  togglePrancha,
  visibilityOptions,
  priorityOptions,
  priorityNoBgOptions,
  typeOptions,
  stageOptions,
  pointsOptions,
  communicationOptions,
  userOptions,
  disciplineOptions,
  disciplines,
  stages,
  allowOnlyStagesLinkedToDisciplinesInTopics,
  createStageLoading,
  userInfo,
  initialViewpoint,
  initialTopicPoints,
  activityViewpointFeature,
  onLoadUrn,
  onActiveUrns,
  onToggleNode,
  onToggleSelectNode,
  onTogglePrancha,
  onToggleViewCamera,
  onToggleView,
  onClearAllToggles,
  onModelLoadError,
  onCreateActivityHandlerReady,
  onRegisterViewpointRestorer,
}) => {
  const { t } = useTranslation('viewer');
  const viewerDiv = useRef<HTMLDivElement>(null);
  const params = useParams();

  const {
    drawerPropertiesVisible,
    viewer,
    showCreateActivityModal,
    handleResize,
    closeDrawerProperties,
    showDrawerProperties,
    handleOpenCreateActivity,
    handleCloseCreateActivity,
    restoreViewpoint,
  } = useInternalViewer({
    viewerDiv,
    loadUrn,
    urns,
    extensionValidateds,
    unloadUrn,
    models,
    views,
    viewpoints,
    pranchas,
    hideNodes,
    toggleNode,
    toggleSelectNode,
    toggleViewCamera,
    toggleView,
    togglePrancha,
    initialViewpoint,
    initialTopicPoints,
    activityViewpointFeature,
    onLoadUrn,
    onActiveUrns,
    onToggleNode,
    onToggleSelectNode,
    onTogglePrancha,
    onToggleViewCamera,
    onToggleView,
    onClearAllToggles,
    onModelLoadError,
  });

  /**
   * O motor de viewpoint da Autodesk atrás do contrato comum, para a modal não
   * depender de `Autodesk.Viewing.GuiViewer3D`. O mapa de urn→nome é preenchido
   * pela própria modal antes de capturar, que é quem tem a lista de arquivos.
   *
   * Recriado quando `showCreateActivityModal` muda: `viewer` é ref, e o motor
   * precisa ler o valor vigente na hora de abrir a modal.
   */
  const viewpointEngine = useMemo(
    () => new AutodeskViewpointEngine(viewer, () => ({})),
    [viewer, showCreateActivityModal],
  );

  // Expose create activity handler to parent component
  useEffect(() => {
    if (onCreateActivityHandlerReady && handleOpenCreateActivity) {
      onCreateActivityHandlerReady(handleOpenCreateActivity);
    }
  }, [onCreateActivityHandlerReady, handleOpenCreateActivity]);

  useEffect(() => {
    if (onRegisterViewpointRestorer && restoreViewpoint) {
      onRegisterViewpointRestorer(restoreViewpoint);
    }
  }, [onRegisterViewpointRestorer, restoreViewpoint]);

  return (
    <Measure onResize={handleResize}>
      {({measureRef}) => 
        <div 
          ref={measureRef}
          className={`${drawerOpen ? styles['viewer'] : styles['customViewer']}`}  
        >
          <div 
            ref={viewerDiv} 
          ></div>
          <ButtonDrawer
            onClick={() => drawerPropertiesVisible ? closeDrawerProperties() : showDrawerProperties()}
            style={{
              position: 'absolute',
              marginTop: 24,
              right: drawerPropertiesVisible ? 326 : 14,
              zIndex: 99,
            }}
            placement={drawerPropertiesVisible ? t('closePanel') : t('expandPanel')}
            border
            delay
          >
            <span className={stylesDrawer['iconToggleWrap']} >
                <Icon icon="avancarDuplo"
                  customSize={10}
                  className={`${stylesDrawer['iconChildren']} ${drawerPropertiesVisible ? stylesDrawer['iconActive'] : ''}`} 
                />
            </span>
          </ButtonDrawer>
          {drawerPropertiesVisible && (
            <PropertiesDrawer
              properties={selectedProperties}
              visible={drawerPropertiesVisible}
              onClose={closeDrawerProperties}
              onShow={showDrawerProperties}
            />
          )}
          <CreateActivityWithViewpoint
            visible={showCreateActivityModal}
            csId={Number(params.constructionSiteId)}
            engine={viewpointEngine}
            visibilityOptions={visibilityOptions}
            priorityOptions={priorityOptions}
            priorityNoBgOptions={priorityNoBgOptions}
            typeOptions={typeOptions}
            stageOptions={stageOptions}
            pointsOptions={pointsOptions}
            communicationOptions={communicationOptions}
            userOptions={userOptions}
            disciplineOptions={disciplineOptions}
            disciplines={disciplines}
            stages={stages}
            allowOnlyStagesLinkedToDisciplinesInTopics={allowOnlyStagesLinkedToDisciplinesInTopics}
            createStageLoading={createStageLoading}
            userInfo={userInfo}
            onCancel={handleCloseCreateActivity}
          />
        </div>
      }
    </Measure>
  );
}

const mapState = (state: IGlobalReducerState) => ({
  ...state.federatedViewer,
  userInfo: state.auth.userInfo,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & IInternalViewer;

export default connector(InternalViewer);
